import React, { useState, useEffect } from "react";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import styles from "../metamask-error-recovery/MetaMaskErrorRecovery.module.scss";

interface OKXErrorRecoveryProps {
  onClose?: () => void;
  autoShow?: boolean;
}

const OKXErrorRecovery: React.FC<OKXErrorRecoveryProps> = ({
  onClose,
  autoShow = false,
}) => {
  const [isVisible, setIsVisible] = useState(autoShow);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { isOKXBrowser, hasWalletExtension } = useDeviceDetection();

  // Auto-show when OKX connection errors occur
  useEffect(() => {
    const handleOKXError = (event: any) => {
      const error = event.detail?.error;
      if (error && isOKXBrowser) {
        // Show for connection declined errors and other common issues
        if (
          error.code === 4001 ||
          error.code === -32002 ||
          error.message?.includes("Connection declined") ||
          error.message?.includes("already pending") ||
          error.message?.includes("Request already pending") ||
          error.message?.includes("Connection can be declined")
        ) {
          console.log("OKXErrorRecovery: Showing recovery for error:", error);
          setIsVisible(true);
        }
      }
    };

    window.addEventListener("okx-error", handleOKXError);
    return () => window.removeEventListener("okx-error", handleOKXError);
  }, [isOKXBrowser]);

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  const handleRefreshPage = () => {
    setIsRefreshing(true);
    window.location.reload();
  };

  const handleRetryConnection = async () => {
    try {
      const okxWallet = (window as any).okxwallet;
      if (okxWallet) {
        // First try to clear any pending requests by checking accounts
        try {
          await okxWallet.request({ method: "eth_accounts" });
        } catch (error) {
          console.log("OKX: Cleared pending state check:", error);
        }

        // Small delay before attempting connection
        await new Promise((resolve) => setTimeout(resolve, 500));

        await okxWallet.request({ method: "eth_requestAccounts" });
        handleClose();
      }
    } catch (error) {
      console.error("OKX retry failed:", error);
      // If retry fails, show a message suggesting page refresh
      alert("Connection retry failed. Please refresh the page and try again.");
    }
  };

  if (!isVisible || !isOKXBrowser) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h3>OKX Wallet Connection Issue</h3>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.errorIcon}>🦄</div>

          <div className={styles.message}>
            <p>
              {!hasWalletExtension
                ? "OKX Wallet is not accessible in this browser session."
                : "OKX Wallet connection was declined or is experiencing issues."}
            </p>

            <div className={styles.explanation}>
              <p>
                <strong>This usually happens when:</strong>
              </p>
              <ul>
                <li>
                  A previous connection request is still pending in OKX Wallet
                </li>
                <li>Multiple connection attempts were made too quickly</li>
                <li>OKX Wallet needs to be refreshed or reinitialized</li>
                <li>The wallet popup was blocked or hidden</li>
              </ul>

              <p>
                <strong>Solutions to try:</strong>
              </p>
              <ul>
                <li>
                  Use the "Retry Connection" button below (includes state reset)
                </li>
                <li>Refresh the page to reinitialize OKX Wallet</li>
                <li>Check if OKX Wallet is enabled in browser settings</li>
                <li>Ensure you're using the latest version of OKX App</li>
                <li>Clear browser cache and try again</li>
              </ul>
            </div>
          </div>

          <div className={styles.actions}>
            <button
              className={`${styles.button} ${styles.primary}`}
              onClick={handleRetryConnection}
              disabled={!hasWalletExtension}
            >
              Retry OKX Connection
            </button>

            <button
              className={`${styles.button} ${styles.secondary}`}
              onClick={handleRefreshPage}
              disabled={isRefreshing}
            >
              {isRefreshing ? "Refreshing..." : "Refresh Page"}
            </button>
          </div>

          <div className={styles.tips}>
            <h4>OKX Wallet Browser Tips:</h4>
            <ul>
              <li>Make sure you're using OKX App's built-in browser</li>
              <li>Try closing and reopening the OKX app</li>
              <li>Check your internet connection</li>
              <li>Update OKX App to the latest version</li>
              <li>Clear OKX browser cache in app settings</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OKXErrorRecovery;
